import React, { useState, useEffect } from "react";
import BotonManualHeader from "../../components/BotonManualHeader";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Table,
  Badge,
  Spinner,
} from "react-bootstrap";
import {
  FaUpload,
  FaDownload,
  FaCheck,
  FaExclamationTriangle,
  FaTimes,
  FaUsers,
  FaBuilding,
  FaFileExcel,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";
import { BiSave, BiGroup, BiBuildingHouse } from "react-icons/bi";
import * as XLSX from "xlsx";
import firebaseApp from "../../firebase/credenciales";
import {
  collection,
  getDocs,
  doc,
  getFirestore,
  updateDoc,
  writeBatch,
  serverTimestamp,
} from "firebase/firestore";
import Swal from "sweetalert2";
import { logBusinessAction } from "../../services/AuditService";

const db = getFirestore(firebaseApp);

const CecosMasivos = ({ usuario }) => {
  const [file, setFile] = useState(null);
  const [excelData, setExcelData] = useState([]);
  const [validationResults, setValidationResults] = useState([]);
  const [users, setUsers] = useState([]);
  const [cecos, setCecos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [warning, setWarning] = useState("");

  const rucEmpresa = usuario.ruc;
  const [isDataLoading, setIsDataLoading] = useState(true);

  // Estadísticas
  const [totalUsuarios, setTotalUsuarios] = useState(0);
  const [totalCecos, setTotalCecos] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      if (rucEmpresa) {
        setIsDataLoading(true);
        try {
          await Promise.all([fetchUsers(), fetchCecos()]);
        } catch (error) {
          console.error("Error cargando datos iniciales:", error);
          setError("Error cargando datos del sistema");
        } finally {
          setIsDataLoading(false);
        }
      }
    };
    fetchData();
  }, [rucEmpresa]);

  useEffect(() => {
    setTotalUsuarios(users.length);
    setTotalCecos(cecos.length);
  }, [users, cecos]);

  const fetchUsers = async () => {
    try {
      const usersCollectionRef = collection(db, "users");
      const usersSnapshot = await getDocs(usersCollectionRef);
      const usersData = [];
      usersSnapshot.forEach((doc) => {
        const userData = doc.data();
        if (userData.activo !== false && userData.rucEmpresa === rucEmpresa) {
          usersData.push({ ...userData, id: doc.id });
        }
      });
      setUsers(usersData);
    } catch (error) {
      console.error("Error al cargar usuarios:", error);
    }
  };

  const fetchCecos = async () => {
    try {
      const cecosRef = collection(db, `cecos/${rucEmpresa}/cecos`);
      const snapshot = await getDocs(cecosRef);
      const cecosData = [];
      snapshot.forEach((doc) => {
        cecosData.push({ id: doc.id, ...doc.data() });
      });
      setCecos(cecosData);
    } catch (error) {
      console.error("Error al cargar CECOs:", error);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (
      !selectedFile.name.endsWith(".xlsx") &&
      !selectedFile.name.endsWith(".xls")
    ) {
      setError("Solo se permiten archivos Excel (.xlsx, .xls)");
      return;
    }

    setFile(selectedFile);
    setError("");
    setSuccess("");
    setWarning("");
  };

  const readExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
          resolve(jsonData);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  const processExcel = async () => {
    if (!file) {
      setError("Por favor seleccione un archivo Excel");
      return;
    }
    setLoading(true);
    setError("");
    setWarning("");

    try {
      const data = await readExcelFile(file);
      setExcelData(data);
      validateData(data);
    } catch (error) {
      console.error("Error al procesar Excel:", error);
      setError("Error al procesar el archivo Excel");
    } finally {
      setLoading(false);
    }
  };

  const validateData = (data) => {
    const results = [];

    if (data.length === 0) {
      setError("El archivo Excel está vacío.");
      return;
    }

    const firstRowKeys = Object.keys(data[0]).map((k) => k.toUpperCase());
    const hasDni = firstRowKeys.some((k) => k.includes("DNI"));
    const hasCodigoCeco = firstRowKeys.some((k) => k.includes("CODIGO_CECO") || k.includes("CODIGO"));

    if (!hasDni || !hasCodigoCeco) {
      setError("El archivo debe contener columnas nombradas 'DNI' y 'CODIGO_CECO'.");
      return;
    }

    data.forEach((row, index) => {
      let dni = "";
      let cecoInput = "";

      Object.keys(row).forEach((key) => {
        if (key.toUpperCase().includes("DNI")) dni = row[key];
        if (key.toUpperCase().includes("CODIGO_CECO") || key.toUpperCase().includes("CODIGO")) cecoInput = row[key];
      });

      const result = {
        row: index + 2,
        dni: dni ? dni.toString().trim() : "",
        cecoInput: cecoInput ? cecoInput.toString().trim() : "",
        errors: [],
        warnings: [],
        valid: true,
        userRef: null,
        cecoRef: null,
      };

      if (!result.dni) {
        result.errors.push("DNI es requerido");
        result.valid = false;
      } else {
        const user = users.find(
          (u) => u.dni && u.dni.toString() === result.dni
        );
        if (!user) {
          result.errors.push("Usuario no encontrado en la base de datos");
          result.valid = false;
        } else {
          result.userName = `${user.name} ${user.apellidos}`;
          result.userRef = user;
        }
      }

      if (!result.cecoInput) {
        result.errors.push("Código de CECO es requerido");
        result.valid = false;
      } else {
        const cecoLower = result.cecoInput.toLowerCase();
        const existingCeco = cecos.find(
          (c) => c.codigo && c.codigo.toLowerCase() === cecoLower
        );

        if (!existingCeco) {
          result.errors.push(`El Código de CECO '${result.cecoInput}' no existe`);
          result.valid = false;
        } else {
          result.cecoRef = existingCeco;
        }
      }

      results.push(result);
    });

    setValidationResults(results);
  };

  const saveAssignments = async () => {
    const validAssignments = validationResults.filter((r) => r.valid);
    if (validAssignments.length === 0) {
      setError("No hay asignaciones válidas para guardar");
      return;
    }

    const result = await Swal.fire({
      title: "Confirmar Asignación",
      text: `¿Estás seguro de asignar el CECO a ${validAssignments.length} usuarios?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, asignar masivamente",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    setProcessing(true);
    setError("");

    try {
      const batch = writeBatch(db);
      let operationCount = 0;
      let batchCount = 0;

      for (const assignment of validAssignments) {
        const userDocRef = doc(db, "users", assignment.userRef.id);

        batch.update(userDocRef, {
          cecoId: assignment.cecoRef.id,
          cecoNombre: assignment.cecoRef.nombre,
          updatedAt: serverTimestamp(),
        });

        operationCount++;

        // Límite de Firebase Batch
        if (operationCount >= 490) {
          await batch.commit();
          batchCount++;
          operationCount = 0;
        }
      }

      if (operationCount > 0) {
        await batch.commit();
      }

      setSuccess(
        `Se han asignado exitosamente los CECOs a ${validAssignments.length} usuarios.`
      );
      Swal.fire({
        icon: "success",
        title: "Proceso completado",
        text: `Se han configurado ${validAssignments.length} CECO(s) correctamente`,
        timer: 3000,
      });

      setValidationResults([]);
      setExcelData([]);
      setFile(null);
      document.getElementById("fileInputCecos").value = "";

      // Registrar auditoría
      await logBusinessAction(
        "CARGA_MASIVA_CECOS",
        "GestionUsuarios",
        { cantidad: validAssignments.length },
        usuario
      );
    } catch (error) {
      console.error("Error guardando asignaciones:", error);
      setError("Error al procesar la asignación masiva en la base de datos.");
    } finally {
      setProcessing(false);
    }
  };

  const downloadTemplate = () => {
    const templateData = [
      { DNI: "12345678", CODIGO_CECO: "CO-1" },
      { DNI: "87654321", CODIGO_CECO: "ADM-01" },
      { DNI: "11223344", CODIGO_CECO: "OP-01" },
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Plantilla");
    XLSX.writeFile(wb, "plantilla_asignacion_cecos.xlsx");
  };

  const validCount = validationResults.filter((r) => r.valid).length;
  const invalidCount = validationResults.filter((r) => !r.valid).length;

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
        {/* Marco principal */}
        <Card
          className="shadow-lg border-0"
          style={{
            borderRadius: "15px",
            overflow: "hidden",
            minHeight: "90vh",
          }}
        >
          {/* Header Premium con degradado azul para Masivos */}
          <Card.Header
            className="text-white py-4 position-relative"
            style={{
              background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
              borderBottom: "3px solid #f59e0b",
            }}
          >
            <div className="position-absolute end-0 top-50 translate-middle-y me-4" style={{ zIndex: 10 }}>
              <BotonManualHeader />
            </div>
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                <BiBuildingHouse className="me-3" />
                Asignación Masiva CECOs
              </h2>
              <p className="mb-0 opacity-75">
                Víncula a los usuarios de manera masiva con Centros de Costo
                existentes mediante un archivo Excel
              </p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {/* Indicador de carga Global para operaciones */}
            {(loading || processing || isDataLoading) && (
              <div
                className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-75"
                style={{ zIndex: 1040 }}
              >
                <div className="text-center">
                  <Spinner
                    animation="border"
                    variant="light"
                    style={{ width: "3rem", height: "3rem" }}
                  />
                  <p className="text-white mt-3 mb-0">
                    {isDataLoading
                      ? "Cargando datos iniciales..."
                      : loading
                      ? "Procesando archivo..."
                      : "Guardando Asignaciones..."}
                  </p>
                  <p className="text-white-50 small">
                    Esto puede tomar unos minutos
                  </p>
                </div>
              </div>
            )}

            {/* Alertas */}
            {error && (
              <Alert
                variant="danger"
                onClose={() => setError("")}
                dismissible
                className="border-0 shadow-sm mb-4"
                style={{ borderRadius: "15px" }}
              >
                {error}
              </Alert>
            )}
            {success && (
              <Alert
                variant="success"
                onClose={() => setSuccess("")}
                dismissible
                className="border-0 shadow-sm mb-4"
                style={{ borderRadius: "15px" }}
              >
                {success}
              </Alert>
            )}
            {warning && (
              <Alert
                variant="warning"
                onClose={() => setWarning("")}
                dismissible
                className="border-0 shadow-sm mb-4"
                style={{ borderRadius: "15px" }}
              >
                <FaExclamationTriangle className="me-2" />
                {warning}
              </Alert>
            )}

            {/* Barra superior con información */}
            <Card
              className="shadow-sm border-0 mb-4"
              style={{ borderRadius: "15px" }}
            >
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col md={1}>
                    <div
                      className="rounded-3 p-3 text-center"
                      style={{
                        background:
                          "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                        color: "white",
                      }}
                    >
                      <BiBuildingHouse size={24} />
                    </div>
                  </Col>
                  <Col md={11}>
                    <h5 className="mb-1 fw-semibold">
                      Asignación Masiva de Centros de Costo
                    </h5>
                    <p className="text-muted mb-0 small">
                      Sube rápidamente el formato en Excel con los DNI y CECOs de tus
                      colaboradores
                    </p>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Estadísticas rápidas */}
            {!isDataLoading && (
              <Row className="mb-4">
                <Col md={6}>
                  <Card
                    className="border-0 shadow-sm"
                    style={{ borderRadius: "15px" }}
                  >
                    <Card.Body
                      className="text-center"
                      style={{
                        background:
                          "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                        color: "white",
                        borderRadius: "15px",
                      }}
                    >
                      <FaUsers size="2rem" className="mb-2" />
                      <h4 className="fw-bold">{totalUsuarios}</h4>
                      <p className="mb-0">Total de Usuarios</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card
                    className="border-0 shadow-sm"
                    style={{ borderRadius: "15px" }}
                  >
                    <Card.Body
                      className="text-center"
                      style={{
                        background:
                          "linear-gradient(135deg, #059669 0%, #10b981 100%)",
                        color: "white",
                        borderRadius: "15px",
                      }}
                    >
                      <FaBuilding size="2rem" className="mb-2" />
                      <h4 className="fw-bold">{totalCecos}</h4>
                      <p className="mb-0">CECOs Disponibles</p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Sección de instrucciones */}
            <Card
              className="mb-4 border-0 shadow-sm"
              style={{ borderRadius: "15px" }}
            >
              <Card.Header
                className="text-white py-3"
                style={{
                  background:
                    "linear-gradient(135deg, #059669 0%, #10b981 100%)",
                  borderRadius: "15px 15px 0 0",
                }}
              >
                <div className="d-flex align-items-center">
                  <FaFileExcel className="me-2" />
                  <h6 className="mb-0 fw-semibold">Instrucciones de Uso</h6>
                </div>
              </Card.Header>
              <Card.Body className="p-4">
                <Row>
                  <Col md={8}>
                    <h6 className="fw-semibold mb-3">Pasos a seguir:</h6>
                    <ol className="mb-3">
                      <li>
                        Descarga la plantilla Excel haciendo clic en el botón
                        "Descargar Plantilla"
                      </li>
                      <li>
                        Completa el archivo con los siguientes datos:
                        <ul className="mt-2 text-muted">
                          <li>
                            <strong>DNI:</strong> DNI del empleado validado (8 o 9
                            dígitos)
                          </li>
                          <li>
                            <strong>CODIGO_CECO:</strong> Código oficial del CECO (p. ej. "ADM-01", "OP-01")
                          </li>
                        </ul>
                      </li>
                      <li>Asegúrate de que el documento no contenga campos con errores tipográficos y que el archivo pese menos de 10MB.</li>
                      <li>
                        Selecciona el archivo pulsando sobre la tarjeta o
                        arrastrándolo y haz clic en "Procesar Excel".
                      </li>
                    </ol>
                  </Col>
                  <Col md={4} className="d-flex align-items-center">
                    <Button
                      variant="success"
                      onClick={downloadTemplate}
                      className="w-100 d-flex align-items-center justify-content-center"
                      style={{
                        borderRadius: "10px",
                        background:
                          "linear-gradient(135deg, #059669 0%, #10b981 100%)",
                        border: "none",
                        boxShadow: "0 4px 8px rgba(16, 185, 129, 0.3)",
                      }}
                    >
                      <FaDownload className="me-2" />
                      Descargar Plantilla
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Área de importación */}
            <Card
              className="mb-4 border-0 shadow-sm"
              style={{ borderRadius: "15px" }}
            >
              <Card.Body className="p-5">
                <div
                  className="rounded-3 p-5 text-center position-relative mb-4"
                  style={{
                    border: file
                      ? "2px dashed #10b981"
                      : "2px dashed #cbd5e1",
                    backgroundColor: file ? "#f0fdf4" : "white",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                  }}
                  onMouseOver={(e) => {
                    if (!file) e.currentTarget.style.backgroundColor = "#f8fafc";
                  }}
                  onMouseOut={(e) => {
                    if (!file) e.currentTarget.style.backgroundColor = "white";
                  }}
                >
                  <input
                    id="fileInputCecos"
                    type="file"
                    accept=".xlsx, .xls"
                    onChange={handleFileChange}
                    className="position-absolute top-0 start-0 w-100 h-100 opacity-0"
                    style={{ cursor: "pointer", zIndex: 10 }}
                    disabled={loading || processing || isDataLoading}
                  />
                  <div className="mb-3">
                    {file ? (
                      <FaFileExcel size={64} className="text-success mb-3" />
                    ) : (
                      <FaUpload size={48} className="text-secondary mb-3 opacity-50" />
                    )}
                  </div>
                  <h5
                    className={
                      file ? "text-success fw-bold" : "text-dark fw-bold"
                    }
                  >
                    {file ? file.name : "Seleccionar Archivo Excel"}
                  </h5>
                  <p className="text-muted small mb-0">
                    {file
                      ? "Archivo listo para procesar"
                      : "Haz clic o arrastra el archivo aquí"}
                  </p>
                </div>

                <div className="text-center">
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={processExcel}
                    disabled={!file || loading || processing || isDataLoading}
                    className="px-5 d-inline-flex align-items-center justify-content-center"
                    style={{
                      borderRadius: "10px",
                      background:
                        "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                      border: "none",
                      boxShadow: "0 4px 12px rgba(59, 130, 246, 0.4)",
                      minWidth: "250px",
                    }}
                  >
                    {loading ? (
                      <>
                        <Spinner size="sm" className="me-2" /> Procesando...
                      </>
                    ) : (
                      <>
                        <FaUpload className="me-2" />
                        Procesar Excel
                      </>
                    )}
                  </Button>
                </div>
              </Card.Body>
            </Card>

            {/* Resultados de Validación */}
            {validationResults.length > 0 && (
              <Card
                className="mb-4 border-0 shadow-sm"
                style={{ borderRadius: "15px" }}
              >
                <Card.Header
                  className="text-white py-3"
                  style={{
                    background:
                      "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                    borderRadius: "15px 15px 0 0",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <FaCheckCircle className="me-2" />
                      <h6 className="mb-0 fw-semibold">Resultados de Validación</h6>
                    </div>
                    <div className="d-flex gap-2">
                      <Badge bg="success" style={{ fontSize: "0.9rem" }}>
                        <FaCheck className="me-1" />
                        Válidos: {validCount}
                      </Badge>
                      <Badge bg="danger" style={{ fontSize: "0.9rem" }}>
                        <FaTimes className="me-1" />
                        Inválidos: {invalidCount}
                      </Badge>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                    <Table
                      responsive
                      hover
                      className="mb-0"
                      style={{ fontSize: "0.9rem" }}
                    >
                      <thead
                        style={{
                          backgroundColor: "#f8f9fa",
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                        }}
                      >
                        <tr>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center">
                            Fila
                          </th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                            DNI
                          </th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                            Usuario
                          </th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                            Código CECO Ingresado
                          </th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center">
                            Estado
                          </th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                            Errores
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {validationResults.map((result, index) => (
                          <tr
                            key={index}
                            style={{ borderBottom: "1px solid #f1f3f4" }}
                            className={result.valid ? "" : "table-danger"}
                          >
                            <td className="text-center py-3 px-3">
                              <Badge
                                bg="dark"
                                className="px-2 py-1"
                                style={{ fontSize: "0.75rem" }}
                              >
                                {result.row}
                              </Badge>
                            </td>
                            <td className="py-3 px-3">
                              <Badge
                                bg="secondary"
                                className="px-2 py-1"
                                style={{ fontSize: "0.75rem" }}
                              >
                                {result.dni}
                              </Badge>
                            </td>
                            <td className="py-3 px-3 text-muted small">
                              {result.userName || "-"}
                            </td>
                            <td className="py-3 px-3 fw-medium">
                              {result.cecoRef ? (
                                <div>
                                    <span className="text-dark fw-bold">{result.cecoRef.nombre}</span> <br/>
                                    <small className="text-secondary">{result.cecoRef.codigo}</small>
                                </div>
                              ) : (
                                result.cecoInput
                              )}
                            </td>
                            <td className="text-center py-3 px-3">
                              <div className="d-flex align-items-center justify-content-center gap-2">
                                {result.valid ? (
                                  <FaCheckCircle
                                    className="text-success"
                                    size="16px"
                                  />
                                ) : (
                                  <FaTimesCircle
                                    className="text-danger"
                                    size="16px"
                                  />
                                )}
                                <Badge
                                  bg={result.valid ? "success" : "danger"}
                                >
                                  {result.valid ? "Válido" : "Inválido"}
                                </Badge>
                              </div>
                            </td>
                            <td className="py-3 px-3">
                              {result.errors.length > 0 && (
                                <ul className="mb-0 small text-danger">
                                  {result.errors.map((error, i) => (
                                    <li key={i}>{error}</li>
                                  ))}
                                </ul>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
                <Card.Footer
                  className="p-3"
                  style={{
                    backgroundColor: "#f8f9fa",
                    borderRadius: "0 0 15px 15px",
                  }}
                >
                  <Button
                    variant="success"
                    size="lg"
                    onClick={saveAssignments}
                    disabled={processing || validCount === 0}
                    className="w-100 d-flex align-items-center justify-content-center"
                    style={{
                      borderRadius: "10px",
                      background:
                        "linear-gradient(135deg, #059669 0%, #10b981 100%)",
                      border: "none",
                      boxShadow: "0 4px 8px rgba(16, 185, 129, 0.3)",
                    }}
                  >
                    {processing ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Guardando asignaciones...
                      </>
                    ) : (
                      <>
                        <BiSave className="me-2" />
                        Guardar {validCount} Asignacion(es)
                      </>
                    )}
                  </Button>
                </Card.Footer>
              </Card>
            )}

            {/* CECOs Existentes */}
            <Card
              className="mb-4 border-0 shadow-sm"
              style={{ borderRadius: "15px" }}
            >
              <Card.Header
                className="text-white py-3"
                style={{
                  background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)",
                  borderRadius: "15px 15px 0 0",
                }}
              >
                <div className="d-flex align-items-center">
                  <FaBuilding className="me-2" />
                  <div>
                    <h6 className="mb-0 fw-semibold">CECOs Existentes</h6>
                    <small className="opacity-75">{cecos.length} Centros de Costo registrados</small>
                  </div>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                {isDataLoading ? (
                  <div className="text-center p-5">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Cargando CECOs...
                  </div>
                ) : cecos.length === 0 ? (
                  <div className="text-center p-5">
                    <FaBuilding size="3rem" className="text-muted mb-3" />
                    <p className="text-muted mb-0">No hay Centros de Costo registrados.</p>
                  </div>
                ) : (
                  <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                    <Table
                      responsive
                      hover
                      className="mb-0"
                      style={{ fontSize: "0.9rem" }}
                    >
                      <thead
                        style={{
                          backgroundColor: "#f8f9fa",
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                        }}
                      >
                        <tr>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                            Código
                          </th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                            Nombre del CECO
                          </th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center">
                            Usuarios Asignados
                          </th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                            Creado
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {cecos.map((ceco) => {
                          const usersInCeco = users.filter(u => u.cecoId === ceco.id).length;
                          return (
                          <tr key={ceco.id} style={{ borderBottom: "1px solid #f1f3f4" }}>
                            <td className="fw-semibold py-3 px-3">
                                <Badge bg="dark" className="px-2 py-1" style={{ fontSize: "0.75rem" }}>
                                  {ceco.codigo}
                                </Badge>
                            </td>
                            <td className="py-3 px-3 fw-medium text-dark">
                              {ceco.nombre}
                            </td>
                            <td className="text-center py-3 px-3">
                              <Badge bg="primary" style={{ fontSize: "0.85rem" }}>
                                {usersInCeco}
                              </Badge>
                            </td>
                            <td className="py-3 px-3 text-muted small">
                              {ceco.createdAt?.toDate
                                ? ceco.createdAt.toDate().toLocaleDateString()
                                : "-"}
                            </td>
                          </tr>
                        )})}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>

          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default CecosMasivos;
